import { mdiHomeOutline } from '@mdi/js';
import { getCategoryKeys } from '@/features/visualizations/domain/panels';
import i18n from '@/i18n';
import { ACCESS_KEYS } from '@/config/entitlementKeys';
import Home from '@/pages/home/Home.vue';
import { normalizeSegmentKey } from './helpers';

// Dev/showcase routes (Sandbox, UI Gallery) are only registered outside of
// production builds. vue-cli exposes `process.env.NODE_ENV`, which is
// statically replaced at build time so the route objects (and their lazy
// imports) are tree-shaken out of production bundles.
const isDev = process.env.NODE_ENV !== 'production';

const devRoutes = [
  {
    meta: {
      title: 'Sandbox',
      requiresAuth: true,
      hideNewsletter: true,
      hideFooter: true,
    },
    path: '/sandbox',
    name: 'sandbox',
    component: () => import('@/pages/sandbox/Sandbox.vue'),
  },
  {
    meta: {
      title: 'UI Gallery',
      requiresAuth: true,
      hideNewsletter: true,
      hideFooter: true,
    },
    path: '/dev/ui-gallery',
    name: 'dev.ui-gallery',
    component: () => import('@/pages/ui-gallery/UiGallery.vue'),
  },
];

export default [
  {
    meta: {
      title: i18n.global.t('page_titles.home'),
      demo: true,
      icon: mdiHomeOutline,
      favoritable: false,
      requiresAuth: true,
      hideNewsletter: true,
      hideFooter: true,
    },
    path: '/',
    name: 'home',
    component: Home,
  },
  {
    path: '/demo/preview',
    name: 'demo-preview',
    meta: { requiresAuth: true, demo: true, hideNewsletter: true, hideFooter: true },
    component: () => import('@/components/demo/DemoLockedPage.vue'),
  },
  {
    // News is now a standalone rail entry (next to Home), not part of
    // the "Data" cauldron — so no `meta.cauldron` here. The News rail
    // button has its own active-state detection in `CauldronRail.vue`.
    meta: {
      title: i18n.global.t('page_titles.news'),
      demo: true,
      requiresAuth: true,
      hideNewsletter: true,
      hideFooter: true,
      favoritable: { kind: 'page', id: 'page:news', label: i18n.global.t('menu_news') },
      breadcrumbs: [{ label: i18n.global.t('menu_news'), to: '/news' }],
    },
    path: '/news',
    name: 'news',
    component: () => import('@/pages/news/NewsEntryPage.vue'),
  },
  {
    // Profiteia's own articles are part of the news feed now (with a "Profiteia" chip); the
    // former separate list keeps its URL for old links.
    path: '/news/profiteia',
    name: 'news-profiteia',
    redirect: { name: 'news' },
  },
  {
    meta: {
      title: i18n.global.t('page_titles.newsroom_reader'),
      demo: true,
      requiresAuth: true,
      hideNewsletter: true,
      hideFooter: true,
      breadcrumbs: [{ label: i18n.global.t('menu_news'), to: '/news' }],
    },
    path: '/news/profiteia/:slug',
    name: 'news-profiteia-article',
    component: () => import('@/features/newsroom-reader/pages/NewsroomArticlePage.vue'),
  },
  {
    // Platform-wide data-source status — lists all ongoing dataset outages.
    // Deep-link target of the compact in-app outage notification (PI-288).
    meta: {
      title: i18n.global.t('page_titles.status'),
      requiresAuth: true,
      hideNewsletter: true,
      hideFooter: true,
      favoritable: { kind: 'page', id: 'page:status', label: i18n.global.t('menu_status') },
      breadcrumbs: [{ label: i18n.global.t('menu_status'), to: '/status' }],
    },
    path: '/status',
    name: 'status',
    component: () => import('@/pages/status/StatusPage.vue'),
  },
  {
    meta: {
      cauldron: 'favourites',
      title: i18n.global.t('favorites_page.title'),
      requiresAuth: true,
      hideNewsletter: true,
      hideFooter: true,
      favoritable: false,
      breadcrumbs: [{ label: i18n.global.t('cauldron.favourites.label'), to: '/favorites' }],
    },
    path: '/favorites',
    name: 'favorites',
    component: () => import('@/pages/favorites/Favorites.vue'),
  },
  {
    meta: {
      title: i18n.global.t('notifications.common.title'),
      requiresAuth: true,
      hideNewsletter: true,
      hideFooter: true,
      favoritable: false,
    },
    path: '/notifications',
    name: 'notifications',
    component: () =>
      import('@/features/notifications/pages/NotificationsPage.vue'),
  },
  ...(isDev ? devRoutes : []),
  {
    // /reports/forward redirects to the first available commodity, which is
    // then rendered by CategoryDashboard with the tab navigation in-page.
    path: '/reports/forward',
    name: 'forward-markets',
    redirect: (to) => {
      const keys = getCategoryKeys('forward');
      const first = keys[0] || 'oil';
      return { path: `/reports/forward/${first}`, query: to.query };
    },
  },
  {
    meta: {
      cauldron: 'forward',
      title: i18n.global.t('page_titles.energy_outlook'),
      requiresAuth: true,
      demo: true,
      requiresEntitlement: ACCESS_KEYS.DATASET_FORWARD,
      hideNewsletter: true,
      hideFooter: true,
      favoritable: (route) => ({
        kind: 'dashboard',
        id: `dashboard:forward/${normalizeSegmentKey(route.params.commodity || '')}`,
      }),
      breadcrumbs: (route, t) => {
        const crumbs = [{ label: t('menu_forward'), to: '/reports/forward' }];
        if (route.params.commodity) {
          const labelKey = `dashboard_titles.${normalizeSegmentKey(route.params.commodity)}`;
          crumbs.push({
            label: t(labelKey),
            to: route.path,
          });
        }
        return crumbs;
      },
    },
    path: '/reports/forward/:commodity',
    name: 'forward',
    component: () => import('../../features/visualizations/pages/CategoryDashboard.vue'),
    props: { category: 'forward' },
  },
  {
    // /reports/flexibility redirects to the first available domain, which is
    // then rendered by CategoryDashboard with the tab navigation in-page.
    path: '/reports/flexibility',
    name: 'flex-markets',
    redirect: (to) => {
      const keys = getCategoryKeys('flex');
      const first = keys[0] || 'intraday';
      return { path: `/reports/flexibility/${first}`, query: to.query };
    },
  },
  {
    meta: {
      cauldron: 'flex',
      title: i18n.global.t('page_titles.dashboard_flexibility_markets'),
      requiresAuth: true,
      demo: true,
      requiresEntitlement: ACCESS_KEYS.DATASET_FLEX,
      hideNewsletter: true,
      hideFooter: true,
      favoritable: (route) => ({
        kind: 'dashboard',
        id: `dashboard:flexibility/${normalizeSegmentKey(route.params.domain || '')}`,
      }),
      breadcrumbs: (route, t) => {
        const crumbs = [{ label: t('menu_flexibility'), to: '/reports/flexibility' }];
        if (route.params.domain) {
          const labelKey = `dashboard_titles.${normalizeSegmentKey(route.params.domain)}`;
          crumbs.push({
            label: t(labelKey),
            to: route.path,
          });
        }
        return crumbs;
      },
    },
    path: '/reports/flexibility/:domain',
    name: 'flex',
    component: () => import('../../features/visualizations/pages/CategoryDashboard.vue'),
    props: { category: 'flex' },
  },
  // `/my-dashboards` used to mount a standalone "dashboard library" page
  // with its own aside + listing. The cockpit refactor folded that listing
  // into Mijn-omgeving's dashboards tab (`MyDashboardsSection.vue`), so
  // the URL now just redirects — same treatment as `/alerts`. The
  // dashboard-creation flow (`/builder/dashboards`, `/dashboards/:id`,
  // etc.) stays mounted; only the umbrella library page is consolidated.
  { path: '/my-dashboards', redirect: '/my-environment/dashboards' },
];
